import { useState, useEffect } from "react";
import type { Column, FilterFn } from "@tanstack/react-table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-react";
import type { FilterType, NumberFilterValue } from "./types";

// Filter functions (generic)
export function createFilterString<T>(): FilterFn<T> {
  return (row, columnId, filterValue: string) => {
    const value = row.getValue(columnId) as string | undefined;
    if (!value) return false;
    return value.toLowerCase().includes(filterValue.toLowerCase());
  };
}

export function createFilterNumber<T>(): FilterFn<T> {
  return (row, columnId, filterValue: NumberFilterValue) => {
    const value = row.getValue(columnId) as number | undefined | null;
    if (value === undefined || value === null) return false;
    
    const { operator, value: filterVal, value2 } = filterValue;
    
    switch (operator) {
      case "gt":
        return filterVal !== undefined && value > filterVal;
      case "lt":
        return filterVal !== undefined && value < filterVal;
      case "eq":
        return filterVal !== undefined && value === filterVal;
      case "between":
        return filterVal !== undefined && value2 !== undefined && value >= filterVal && value <= value2;
      default:
        return true;
    }
  };
}

export function createFilterEnum<T>(): FilterFn<T> {
  return (row, columnId, filterValue: string) => {
    const value = row.getValue(columnId) as string | undefined | null;
    
    // Special case: filter for empty/null values
    if (filterValue === "__empty__") {
      return value === undefined || value === null || value === "";
    }
    
    // Normal case: match exact value
    return value === filterValue;
  };
}

// Filter UI Components (generic)
export function StringFilter<T>({ column }: { column: Column<T, unknown> }) {
  const filterValue = (column.getFilterValue() as string) || "";
  const [inputValue, setInputValue] = useState(filterValue);
  
  // Update input value when filter value changes externally
  useEffect(() => {
    setInputValue(filterValue);
  }, [filterValue]);
  
  // Debounce filter updates
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue !== filterValue) {
        column.setFilterValue(inputValue || undefined);
      }
    }, 400);
    
    return () => clearTimeout(timeoutId);
  }, [inputValue, filterValue, column]);
  
  return (
    <Input
      placeholder="Filter..."
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      className="h-8 w-[150px]"
    />
  );
}

export function NumberFilter<T>({ column }: { column: Column<T, unknown> }) {
  const filterValue = (column.getFilterValue() as NumberFilterValue | undefined);
  const [operator, setOperator] = useState<"gt" | "lt" | "eq" | "between">(filterValue?.operator || "gt");
  const [value, setValue] = useState<string>(filterValue?.value?.toString() || "");
  const [value2, setValue2] = useState<string>(filterValue?.value2?.toString() || "");

  // Update input values when filter value changes externally
  useEffect(() => {
    const currentFilter = column.getFilterValue() as NumberFilterValue | undefined;
    if (currentFilter) {
      setOperator(currentFilter.operator);
      setValue(currentFilter.value?.toString() || "");
      setValue2(currentFilter.value2?.toString() || "");
    } else {
      setValue("");
      setValue2("");
    }
  }, [column]);

  const updateFilter = (op: "gt" | "lt" | "eq" | "between", val: string, val2?: string) => {
    if (op === "between") {
      if (val && val2) {
        const num1 = Number(val);
        const num2 = Number(val2);
        if (!isNaN(num1) && !isNaN(num2)) {
          column.setFilterValue({
            operator: op,
            value: Math.min(num1, num2),
            value2: Math.max(num1, num2),
          });
        }
      } else if (!val && !val2) {
        column.setFilterValue(undefined);
      }
    } else {
      if (val) {
        const num = Number(val);
        if (!isNaN(num)) {
          column.setFilterValue({
            operator: op,
            value: num,
          });
        }
      } else {
        column.setFilterValue(undefined);
      }
    }
  };

  // Debounce value changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const currentFilter = column.getFilterValue() as NumberFilterValue | undefined;
      const currentValue = currentFilter?.value?.toString() || "";
      const currentValue2 = currentFilter?.value2?.toString() || "";
      
      // Only update if the values have actually changed
      if (value !== currentValue || (operator === "between" && value2 !== currentValue2)) {
        if (operator === "between") {
          updateFilter(operator, value, value2);
        } else {
          updateFilter(operator, value);
        }
      }
    }, 400);
    
    return () => clearTimeout(timeoutId);
  }, [value, value2, operator, column]);

  const handleOperatorChange = (op: "gt" | "lt" | "eq" | "between") => {
    setOperator(op);
    // Operator changes are immediate (Select dropdown, no typing)
    if (op === "between") {
      updateFilter(op, value, value2 || value);
    } else {
      updateFilter(op, value);
    }
  };

  const handleValueChange = (val: string) => {
    setValue(val);
    // Don't call updateFilter here - let the debounced effect handle it
  };

  const handleValue2Change = (val: string) => {
    setValue2(val);
    // Don't call updateFilter here - let the debounced effect handle it
  };

  const clearFilter = () => {
    setValue("");
    setValue2("");
    setOperator("gt");
    column.setFilterValue(undefined);
  };

  return (
    <div className="space-y-2 w-[200px]">
      <Select value={operator} onValueChange={handleOperatorChange}>
        <SelectTrigger className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="gt">Greater than</SelectItem>
          <SelectItem value="lt">Less than</SelectItem>
          <SelectItem value="eq">Equals</SelectItem>
          <SelectItem value="between">Between</SelectItem>
        </SelectContent>
      </Select>
      <Input
        type="number"
        placeholder="Value"
        value={value}
        onChange={(e) => handleValueChange(e.target.value)}
        className="h-8"
      />
      {operator === "between" && (
        <Input
          type="number"
          placeholder="Max value"
          value={value2}
          onChange={(e) => handleValue2Change(e.target.value)}
          className="h-8"
        />
      )}
      <Button size="sm" variant="outline" onClick={clearFilter} className="w-full h-8">
        <X className="h-4 w-4 mr-2" />
        Clear
      </Button>
    </div>
  );
}

export function EnumFilter<T>({ column, enumValues }: { column: Column<T, unknown>; enumValues: Record<string, string> }) {
  const filterValue = (column.getFilterValue() as string);
  const displayValue = filterValue || "__all__";
  
  return (
    <Select
      value={displayValue}
      onValueChange={(value) => {
        if (value === "__all__") {
          column.setFilterValue(undefined);
        } else {
          column.setFilterValue(value);
        }
      }}
    >
      <SelectTrigger className="h-8 w-[150px]">
        <SelectValue placeholder="All" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">All</SelectItem>
        <SelectItem value="__empty__">No value</SelectItem>
        {Object.entries(enumValues).map(([key, enumValue]) => (
          <SelectItem key={key} value={enumValue}>
            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

